import { useCallback, useRef, useState } from 'react';

/**
 * Custom hook that manages a transition state using both ref and state.
 * This pattern is useful when you need:
 * - Immediate, synchronous checks (via ref)
 * - Re-render triggers when transition completes (via state)
 *
 * Common use case: Preventing actions during async operations while ensuring
 * UI updates when the operation completes.
 */
export const useTransitionState = (initialValue = false) => {
  const transitionRef = useRef(initialValue);
  const [isTransitioning, setIsTransitioning] = useState(initialValue);

  const startTransition = useCallback(() => {
    transitionRef.current = true;
    setIsTransitioning(true);
  }, []);

  const endTransition = useCallback(() => {
    transitionRef.current = false;
    setIsTransitioning(false);
  }, []);

  return {
    // For use in effects - check transitionRef.current directly
    transitionRef,
    // For use in dependencies to trigger re-renders
    isTransitioning,
    // Methods to control transitions
    startTransition,
    endTransition,
  };
};

export type TransitionState = ReturnType<typeof useTransitionState>;
